import React, { useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSmile, faUser, faExclamationCircle, faCar, faPaw, faMapMarkerAlt,
    faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import api from "../../../api/api"; // Adjust path as needed

const CancelDelivery = ({ delivery, isOpen, onCancel }) => {
    const [selectedReason, setSelectedReason] = useState("");
    const [customReason, setCustomReason] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const reasons = [
        { label: "Payment method not provided", icon: faExclamationTriangle },
        { label: "Vehicle not available", icon: faCar }, // You may need to import faCar
        { label: "Driver not available", icon: faUser },
        { label: "Too many pets for assigned vehicle", icon: faPaw }, // faPaw for pet-related
        { label: "Incorrect delivery address", icon: faMapMarkerAlt }, // Import faMapMarkerAlt
        { label: "Other", icon: faExclamationCircle }, // For custom reason
    ];

    const handleCancel = async () => {
        setIsSubmitting(true);
        const cancelReason = selectedReason === "Other" ? customReason : selectedReason;
        try {
            await api.post(`/deliveries/${delivery.id}`, {
                _method: 'PUT',
                cancel_reason: cancelReason,
                deliveryCancelUser: true,
            });
            toast.success("Delivery canceled successfully.");
            closeModal();
        } catch (error) {
            toast.error("Failed to cancel delivery.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeModal = () => {
        setSelectedReason("");
        setCustomReason("");
        onCancel();
    };

    return (
        <>
            <Transition appear show={isOpen} as={React.Fragment}>
                <Dialog as="div" className="relative z-50" onClose={closeModal}>
                    {/* Background overlay */}
                    <Transition.Child
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />
                    </Transition.Child>

                    {/* Modal panel */}
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={React.Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-lg bg-white p-4 sm:p-6 text-left align-middle shadow-xl transition-all mx-2 sm:mx-0">
                                    <Dialog.Title as="h3" className="text-xl sm:text-2xl font-bold text-purple-700 mb-3 sm:mb-4">
                                        Are you sure you want to cancel user Pet delivery? 🚚
                                    </Dialog.Title>

                                    {/* Encouraging message */}
                                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                                        Assess if the delivery can still be completed with minor adjustments. Quick reassignment may prevent cancellation and preserve client trust                                    </p>

                                    {/* Reconsideration message */}
                                    <div className="bg-purple-50 p-3 sm:p-4 rounded-md mb-4 sm:mb-6 flex items-start sm:items-center space-x-3 sm:space-x-4">
                                        <FontAwesomeIcon icon={faSmile} className="text-purple-500 text-2xl sm:text-3xl flex-shrink-0" />
                                        <p className="text-sm sm:text-base text-purple-700">
                                            Cancellations may disrupt schedules and client expectations. Before proceeding, evaluate if rescheduling or assigning a different driver could resolve the issue more efficiently.
                                        </p>
                                    </div>

                                    {/* Reason selection */}
                                    <h4 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-3">
                                        Why do you want to cancel?
                                    </h4>
                                    <div className="space-y-2">
                                        {reasons.map((reason, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setSelectedReason(reason.label)}
                                                className={`flex items-center w-full p-2 sm:p-3 rounded-md border text-sm sm:text-base
                                                    ${selectedReason === reason.label
                                                        ? 'bg-purple-100 border-purple-500'
                                                        : 'border-gray-300'
                                                    } hover:bg-purple-50 transition-colors`}
                                            >
                                                <FontAwesomeIcon
                                                    icon={reason.icon}
                                                    className="text-purple-500 mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5"
                                                />
                                                <span className="text-gray-700">{reason.label}</span>
                                            </button>
                                        ))}
                                        {selectedReason === "Other" && (
                                            <textarea
                                                placeholder="Enter custom reason"
                                                value={customReason}
                                                onChange={(e) => setCustomReason(e.target.value)}
                                                className="w-full mt-2 px-3 py-2 border rounded-md focus:outline-none focus:border-purple-500 text-sm sm:text-base"
                                                rows={3}
                                            />
                                        )}
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-4 sm:mt-6">
                                        <button
                                            onClick={closeModal}
                                            className="w-full sm:w-auto bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 text-gray-700 text-sm sm:text-base order-2 sm:order-1"
                                        >
                                            Go Back
                                        </button>
                                        <button
                                            onClick={handleCancel}
                                            disabled={!selectedReason || (selectedReason === "Other" && !customReason)}
                                            className={`w-full sm:w-auto px-4 py-2 rounded-lg text-white text-sm sm:text-base order-1 sm:order-2
                                                ${isSubmitting
                                                    ? "bg-red-400"
                                                    : "bg-red-500 hover:bg-red-600"
                                                }`}
                                        >
                                            {isSubmitting ? "Cancelling..." : "Confirm Cancellation"}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
};

export default CancelDelivery;
